import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Popover,
  IconButton,
  Tooltip,
} from "@mui/material";
import { PiSortDescendingLight, PiSortAscendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { MdSettings, MdDragIndicator, MdFileDownload } from "react-icons/md";
import { SelectChangeEvent } from "@mui/material/Select";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import authService from "../../../utils/authService";
import * as XLSX from "xlsx";
import { 
  ColumnConfig, 
  getDefaultLearningProgressColumns, 
  loadColumnVisibility, 
  updateColumnVisibility, 
  resetColumnVisibility,
  PAGE_KEYS 
} from "../../../utils/columnVisibilityUtils";
import { compareTimeliness, isTimelinessSortField } from "../../../utils/timelinessSortUtils";
import { compareNumericScore, compareMaybeNumeric, isScoreSortField } from "../../../utils/numericSortUtils";

interface Project {
  project_id: number;
  batch_project_id: number;
  batch_project_name: string;
}

interface FiltersData {
  courses: string[];
  batches: {
    [course: string]: string[];
  };
  subjects: {
    [course: string]: {
      [batch: string]: string[];
    };
  };
  projects?: {
    [course: string]: {
      [batch: string]: Project[];
    };
  };
}

interface Student {
  ID: string;
  Name: string;
  USN: string;
  Student_Type: string;
  College: string;
  Branch: string;
  Mobile: string;
  Score: string;
  "Score_%": string;
  Catogory: string;
  College_Rank: number;
  Overall_Rank: number;
  Email: string;
  No_of_hours: string | null;
  no_of_hours_should_be_completed_by_current_day?: string | null;
  Course: string;
  Batch: string;
  Subject: string[];
  Attendance: string;
  Delay_in_days: number;
  Delays?: number;
  Performance_Type?: string;
  progress_percentage_of_should_be_completed_subtasks?: number;
  performance_percentage?: number;
  project_name?: string;
}

const LearningProgress: React.FC = () => {
  const navigate = useNavigate();
  
  // Authentication check - redirect to login if not authenticated as Coordinator
  useEffect(() => {
    if (!sessionStorage.getItem("Coordinator")) {
      navigate('/');
      return;
    }
  }, [navigate]);

  const [filtersData, setFiltersData] = useState<FiltersData | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [selectedSubject, setSelectedSubject] = useState<string>("");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: string;
  }>({ key: "", direction: "" });
  const [columns, setColumns] = useState<ColumnConfig[]>(() => 
    loadColumnVisibility(PAGE_KEYS.LEARNING_PROGRESS, getDefaultLearningProgressColumns())
  );
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null
  );
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(
    new Set()
  );
  const [totalTimeToBeSpent, setTotalTimeToBeSpent] = useState<string>("");
  const location = useLocation();

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    if (location.state && filtersData) {
      const state = location.state as {
        category?: string;
        direction?: "asc" | "desc";
        course?: string;
        batch?: string;
        subject?: string;
        project?: Project | string | null;
        fromSidebar?: boolean;
      };
      const { category, direction, course, batch, subject, project, fromSidebar } = state;

      if (course && filtersData.courses.includes(course)) {
        setSelectedCourse(course);
        if (batch && filtersData.batches[course]?.includes(batch)) {
          setSelectedBatch(batch);
          if (subject && filtersData.subjects[course]?.[batch]?.includes(subject)) {
            setSelectedSubject(subject);
            setSelectedProject(null);
          } else if (project) {
            const list = filtersData.projects?.[course]?.[batch];
            const proj = project as Project;
            let resolved: Project | null = null;
            if (list?.length) {
              resolved = typeof proj === "object" && proj?.project_id != null
                ? list.find((p) => String(p.project_id) === String(proj.project_id) && String(p.batch_project_id) === String(proj.batch_project_id)) ?? null
                : list.find((p) => p.batch_project_name === project) ?? null;
            }
            if (!resolved && typeof proj === "object" && proj?.project_id != null && proj?.batch_project_id != null && proj?.batch_project_name) {
              resolved = { project_id: Number(proj.project_id), batch_project_id: Number(proj.batch_project_id), batch_project_name: String(proj.batch_project_name) };
            }
            if (resolved) {
              setSelectedProject(resolved);
              setSelectedSubject("");
            }
          }
        }
      }
      if (category && !fromSidebar) setSelectedCategory(category);

      if (!fromSidebar && category) {
        const progressDirection =
          direction ??
          (category === "behind" ? "asc" : category === "ahead" ? "desc" : undefined);
        if (progressDirection === "asc") {
          setSortConfig({ key: "performance_percentage", direction: "ascending" });
        } else if (progressDirection === "desc") {
          setSortConfig({ key: "performance_percentage", direction: "descending" });
        } else {
          setSortConfig({ key: "", direction: "" });
        }
      }
    }
  }, [location.state, filtersData]);

  useEffect(() => {
    // If coming from sidebar (no category), only require course, batch, subject OR project
    // If coming from Show More (with category), require all filters
    const fromSidebar = location.state?.fromSidebar;
    const hasRequiredFilters = fromSidebar
      ? selectedCourse && selectedBatch && (selectedSubject || selectedProject)
      : selectedCourse && selectedBatch && (selectedSubject || selectedProject) && selectedCategory;

    if (hasRequiredFilters) {
      fetchLearningProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse, selectedBatch, selectedSubject, selectedProject, selectedCategory]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedStudents(new Set());
  }, [selectedCourse, selectedBatch, selectedSubject, selectedProject, selectedCategory]);

  // Save LearningProgress filters to session storage whenever they change
  useEffect(() => {
    if (selectedCourse || selectedBatch || selectedSubject || selectedProject) {
      const filters = {
        course: selectedCourse,
        batch: selectedBatch,
        subject: selectedSubject,
        project: selectedProject,
      };
      sessionStorage.setItem(
        "learningProgressFilters",
        JSON.stringify(filters)
      );
    }
  }, [
    selectedCourse,
    selectedBatch,
    selectedSubject,
    selectedProject,
  ]);

  const fetchFilters = async () => {
    try {
      const userId = sessionStorage.getItem("Id");
      if (!userId) {
        console.error("User ID not found in session storage");
        return;
      }
      const response = await authService.get(`coordinator/filters/${userId}/`);
      setFiltersData(response.data);

      const navState = location.state as { course?: string; batch?: string; subject?: string; project?: unknown } | null;
      const hasNavFilters = navState?.course && navState?.batch && (navState?.subject || navState?.project);
      const savedFilters = hasNavFilters ? null : sessionStorage.getItem("learningProgressFilters");
      if (savedFilters) {
        try {
          const filters = JSON.parse(savedFilters);
          if (filters.course && response.data.courses.includes(filters.course)) {
            setSelectedCourse(filters.course);
            if (
              filters.batch &&
              response.data.batches[filters.course]?.includes(filters.batch)
            ) {
              setSelectedBatch(filters.batch);
              // Restore subject OR project (mutually exclusive)
              if (
                filters.subject &&
                response.data.subjects[filters.course]?.[filters.batch]?.includes(filters.subject)
              ) {
                setSelectedSubject(filters.subject);
                setSelectedProject(null);
              } else if (filters.project && response.data.projects?.[filters.course]?.[filters.batch]) {
                const list = response.data.projects[filters.course][filters.batch];
                const project = typeof filters.project === "object" && filters.project?.project_id != null
                  ? list.find((p: Project) => String(p.project_id) === String(filters.project.project_id) && String(p.batch_project_id) === String(filters.project.batch_project_id))
                  : list.find((p: Project) => p.batch_project_name === filters.project);
                if (project) {
                  setSelectedProject(project);
                  setSelectedSubject("");
                }
              }
            }
          }
        } catch (error) {
          console.error("Error parsing saved learning progress filters:", error);
        }
      }
      
      if (!selectedCourse && !savedFilters && !hasNavFilters && response.data.courses.length > 0) {
        setSelectedCourse(response.data.courses[0]);
        if (response.data.batches[response.data.courses[0]]?.length > 0) {
          const batch = response.data.batches[response.data.courses[0]][0];
          setSelectedBatch(batch);
          // Only set subject OR project, not both - prefer subject first
          if (
            response.data.subjects[response.data.courses[0]]?.[batch]?.length > 0
          ) {
            setSelectedSubject(
              response.data.subjects[response.data.courses[0]][batch][0]
            );
          } else if (
            response.data.projects?.[response.data.courses[0]]?.[batch]?.length > 0
          ) {
            setSelectedProject(
              response.data.projects[response.data.courses[0]][batch][0]
            );
          }
        }
      }
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const fetchLearningProgress = async () => {
    try {
      setIsLoading(true);

      // If no category is selected (from sidebar), show all students
      if (!selectedCategory) {
        setAllStudents([]);
        setStudents([]);
        setIsLoading(false);
        return;
      }

      const payload: Record<string, unknown> = {
        course_name: selectedCourse,
        batch_name: selectedBatch,
        type_of_progress: selectedCategory,
        user_id: sessionStorage.getItem("Id"),
      };
      if (selectedSubject) {
        payload.subject_name = selectedSubject;
        payload.project_id = null;
        payload.project_name = "";
      } else if (selectedProject) {
        payload.subject_name = "";
        payload.project_id = selectedProject.project_id;
        payload.project_name = selectedProject.batch_project_name;
      } else {
        payload.subject_name = "";
        payload.project_id = null;
        payload.project_name = "";
      }
      const response = await authService.post(
        `coordinator/learning_progress/`,
        payload
      );
      const data = response.data.data;
      setTotalTimeToBeSpent(response?.data?.total_time_to_be_spent ?? "");
      // Handle the response format with students array wrapper
      if (data && data.students && Array.isArray(data.students)) {
        setAllStudents(data.students);
        setStudents(data.students);
      } else if (data && Array.isArray(data)) {
        // Fallback for direct array format
        setAllStudents(data);
        setStudents(data);
      } else {
        setAllStudents([]);
        setStudents([]);
      }
    } catch (error) {
      console.error("Error fetching learning progress:", error);
      setError(
        "Failed to load learning progress data. Please try refreshing the page."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCourseChange = (event: SelectChangeEvent<string>) => {
    const course = event.target.value;
    setSelectedCourse(course);
    setSelectedBatch("");
    setSelectedSubject("");
    setSelectedProject(null);
    setSelectedCategory("");
    setError(null);

    setAllStudents([]);
    setStudents([]);
    // Set default batch for new course
    if (
      filtersData &&
      filtersData.batches[course] &&
      filtersData.batches[course].length > 0
    ) {
      setSelectedBatch(filtersData.batches[course][0]);
      // Only set subject OR project, not both - prefer subject first
      const batch = filtersData.batches[course][0];
      if (
        filtersData.subjects[course] &&
        filtersData.subjects[course][batch] &&
        filtersData.subjects[course][batch].length > 0
      ) {
        setSelectedSubject(filtersData.subjects[course][batch][0]);
      } else if (
        filtersData.projects?.[course] &&
        filtersData.projects[course][batch] &&
        filtersData.projects[course][batch].length > 0
      ) {
        setSelectedProject(filtersData.projects[course][batch][0]);
      }
    }
  };

  const handleBatchChange = (event: SelectChangeEvent<string>) => {
    const batch = event.target.value;
    setSelectedBatch(batch);
    setSelectedSubject("");
    setSelectedProject(null);
    setSelectedCategory("");
    setError(null);
    setAllStudents([]);
    setStudents([]);
    // Only set subject OR project, not both - prefer subject first
    if (
      filtersData &&
      filtersData.subjects[selectedCourse] &&
      filtersData.subjects[selectedCourse][batch] &&
      filtersData.subjects[selectedCourse][batch].length > 0
    ) {
      setSelectedSubject(filtersData.subjects[selectedCourse][batch][0]);
    } else if (
      filtersData &&
      filtersData.projects?.[selectedCourse] &&
      filtersData.projects[selectedCourse][batch] &&
      filtersData.projects[selectedCourse][batch].length > 0
    ) {
      setSelectedProject(filtersData.projects[selectedCourse][batch][0]);
    }
  };

  const handleSubjectChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedSubject(value);
    // Always clear project when subject is selected (mutually exclusive)
    setSelectedProject(null);
    setSelectedCategory("");
    setError(null);
    setAllStudents([]);
    setStudents([]);
  };

  const handleProjectChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (!value || !filtersData?.projects?.[selectedCourse]?.[selectedBatch]) {
      setSelectedProject(null);
    } else {
      const project = filtersData.projects[selectedCourse][selectedBatch].find(
        (p) => `${p.project_id}_${p.batch_project_id}` === value
      );
      setSelectedProject(project ?? null);
    }
    // Always clear subject when project is selected (mutually exclusive)
    setSelectedSubject("");
    setSelectedCategory("");
    setError(null);
    setAllStudents([]);
    setStudents([]);
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setSelectedCategory(event.target.value);
    setError(null);
  };

  const handleColumnVisibilityChange = (columnKey: string) => {
    const updatedColumns = updateColumnVisibility(PAGE_KEYS.LEARNING_PROGRESS, columns, columnKey);
    setColumns(updatedColumns);
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  const handleDragStart = (e: React.DragEvent, columnKey: string) => {
    setDraggedColumn(columnKey);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== columnKey) {
      setDragOverColumn(columnKey);
    }
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumnKey: string) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== targetColumnKey) {
      const draggedIndex = columns.findIndex(
        (col) => col.key === draggedColumn
      );
      const targetIndex = columns.findIndex(
        (col) => col.key === targetColumnKey
      );

      const newColumns = [...columns];
      const [draggedItem] = newColumns.splice(draggedIndex, 1);
      newColumns.splice(targetIndex, 0, draggedItem);

      setColumns(newColumns);
    }
    setDraggedColumn(null);
    setDragOverColumn(null);
    setIsDragging(false);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
    setIsDragging(false);
  };

  const handleSort = (key: string) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedStudents = () => {
    let filtered = [...students];

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((student) => {
        return Object.values(student).some((value) => {
          if (value === null || value === undefined) return false;
          return value.toString().toLowerCase().includes(query);
        });
      });
    }

    if (selectedCategory) {
      filtered = filtered.sort((a, b) => {
        if (a.Catogory === selectedCategory && b.Catogory !== selectedCategory)
          return -1;
        if (a.Catogory !== selectedCategory && b.Catogory === selectedCategory)
          return 1;
        return 0;
      });
    }

    if (sortConfig.key) {
      filtered = filtered.sort((a, b) => {
        const aVal = a[sortConfig.key as keyof Student];
        const bVal = b[sortConfig.key as keyof Student];
        const aEmpty = aVal === null || aVal === undefined;
        const bEmpty = bVal === null || bVal === undefined;

        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return sortConfig.direction === "ascending" ? -1 : 1;
        if (bEmpty) return sortConfig.direction === "ascending" ? 1 : -1;

        if (isTimelinessSortField(sortConfig.key)) {
          return compareTimeliness(
            aVal as string,
            bVal as string,
            sortConfig.direction
          );
        }

        if (isScoreSortField(sortConfig.key)) {
          return compareNumericScore(aVal, bVal, sortConfig.direction);
        }

        return compareMaybeNumeric(aVal, bVal, sortConfig.direction);
      });
    }

    return filtered;
  };

  const handleViewStudent = (student: Student) => {
    if (location.pathname.includes("admin")) {
      navigate("/admin/performance-analysis", {
        state: { studentInfo: student },
      });
    } else if (location.pathname.includes("trainer")) {
      navigate("/trainer/performance-analysis", {
        state: { studentInfo: student },
      });
    } else if (location.pathname.includes("coordinator")) {
      navigate("/coordinator/performance-analysis", {
        state: { studentInfo: student },
      });
    } else {
      navigate("/", { state: { studentInfo: student } });
    }
  };

  const handleExportToExcel = () => {
    const dataToExport =
      selectedStudents.size > 0
        ? sortedStudents().filter((student) => selectedStudents.has(student.ID))
        : sortedStudents();
    const firstStudent = dataToExport[0];
    const progressHeaderLabel =
      firstStudent?.progress_percentage_of_should_be_completed_subtasks != null
        ? `Progress (${firstStudent.progress_percentage_of_should_be_completed_subtasks}%)`
        : "Progress";

    // Filter data to only include visible columns
    const visibleColumns = columns.filter(
      (col) => col.visible && col.key !== "actions"
    );
    const filteredData = dataToExport.map((student) => {
      const filteredStudent: any = {};
      visibleColumns.forEach((column) => {
        if (column.key === "No_of_hours") {
          const noHours = student.No_of_hours ?? "";
          const noOfHoursHeader = `${column.label} (${totalTimeToBeSpent || "--"})`;
          filteredStudent[noOfHoursHeader] = noHours || "--";
        } else if (column.key === "performance_percentage") {
          filteredStudent[progressHeaderLabel] =
            student.performance_percentage != null
              ? `${student.performance_percentage}%`
              : "--";
        } else if (column.key === "Score" || column.key === "Score_%") {
          filteredStudent[column.label] =
            student[column.key as keyof Student] === "--/--"
              ? "--"
              : student[column.key as keyof Student];
        } else if (column.key === "CGPA") {
          filteredStudent[column.label] =
            student[column.key as keyof Student] !== null &&
            student[column.key as keyof Student] !== undefined &&
            student[column.key as keyof Student] !== ""
              ? student[column.key as keyof Student]
              : "--";
        } else {
          filteredStudent[column.label] = student[column.key as keyof Student];
        }
      });
      return filteredStudent;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "learning_progress.xlsx");
  };

  const handleSelectStudent = (studentId: string) => {
    setSelectedStudents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const currentStudents = sortedStudents();
    if (selectedStudents.size === currentStudents.length && currentStudents.length > 0) {
      // Deselect all
      setSelectedStudents(new Set());
    } else {
      // Select all
      setSelectedStudents(
        new Set(currentStudents.map((student) => student.ID))
      );
    }
  };

  return (
    <div className="container-fluid p-0 pe-2" style={{ marginTop: "12px" }}>
      <Card>
        <CardContent
          style={{
            height: `calc(100vh - 15px)`,
            overflow: "hidden",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            mb={1}
            style={{ flexShrink: 0 }}
          >
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}
            >
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Course
                </InputLabel>
                <Select
                  value={selectedCourse}
                  onChange={handleCourseChange}
                  label="Course"
                  disabled={isLoading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Course
                  </MenuItem>
                  {filtersData?.courses.map((course) => (
                    <MenuItem
                      key={course}
                      value={course}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {course}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Batch
                </InputLabel>
                <Select
                  value={selectedBatch}
                  onChange={handleBatchChange}
                  label="Batch"
                  disabled={!selectedCourse || isLoading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Batch
                  </MenuItem>
                  {filtersData?.batches[selectedCourse]?.map((batch) => (
                    <MenuItem
                      key={batch}
                      value={batch}
                      style={{ fontSize: "14px", fontFamily: "inherit" }}
                    >
                      {batch}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Subject
                </InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={handleSubjectChange}
                  label="Subject"
                  disabled={!selectedBatch || isLoading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Subject
                  </MenuItem>
                  {filtersData?.subjects[selectedCourse]?.[selectedBatch]?.map(
                    (subject) => (
                      <MenuItem
                        key={subject}
                        value={subject}
                        style={{ fontSize: "14px", fontFamily: "inherit" }}
                      >
                        {subject}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
              <span style={{ fontSize: "14px", fontWeight: 600, color: "#666", margin: "0 4px" }}>
                OR
              </span>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Project
                </InputLabel>
                <Select
                  value={selectedProject ? `${selectedProject.project_id}_${selectedProject.batch_project_id}` : ""}
                  onChange={handleProjectChange}
                  label="Project"
                  disabled={!selectedBatch || isLoading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Project
                  </MenuItem>
                  {filtersData?.projects?.[selectedCourse]?.[selectedBatch]?.map(
                    (project) => (
                      <MenuItem
                        key={`${project.project_id}_${project.batch_project_id}`}
                        value={`${project.project_id}_${project.batch_project_id}`}
                        style={{ fontSize: "14px", fontFamily: "inherit" }}
                      >
                        {project.batch_project_name}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 140 }}
              >
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>
                  Category
                </InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  label="Category"
                  disabled={(!selectedSubject && !selectedProject) || isLoading}
                  style={{
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  <MenuItem
                    value=""
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Select Category
                  </MenuItem>
                  <MenuItem
                    value="ahead"
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Ahead
                  </MenuItem>
                  <MenuItem
                    value="on_track"
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    On-time
                  </MenuItem>
                  <MenuItem
                    value="behind"
                    style={{ fontSize: "14px", fontFamily: "inherit" }}
                  >
                    Laggards
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <FormControl variant="outlined" size="small">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    minWidth: "200px",
                    height: "36px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                />
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExportToExcel}
                disabled={sortedStudents().length === 0}
                startIcon={<MdFileDownload />}
                style={{
                  height: "36px",
                  fontSize: "12px",
                  textTransform: "none",
                  border: "1px solid #ccc",
                  color: "#666",
                  whiteSpace: "nowrap",
                }}
              >
                Export{" "}
                {selectedStudents.size > 0 ? `(${selectedStudents.size})` : ""}
              </Button>
              <IconButton
                onClick={handleSettingsClick}
                size="small"
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "6px",
                  height: "36px",
                  width: "36px",
                }}
              >
                <MdSettings size={18} />
              </IconButton>
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            style={{ 
              marginTop: "8px", 
              flexGrow: 1, 
              overflow: "auto",
              position: "relative"
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRight: "1px solid #e0e0e0",
                      fontWeight: 600,
                      fontSize: "12px",
                      padding: "8px 12px",
                      minWidth: "50px",
                      textAlign: "center",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      pointerEvents: "auto",
                    }}
                  >
                    <Checkbox
                      checked={
                        selectedStudents.size === sortedStudents().length &&
                        sortedStudents().length > 0
                      }
                      indeterminate={
                        selectedStudents.size > 0 &&
                        selectedStudents.size < sortedStudents().length
                      }
                      onChange={handleSelectAll}
                      size="small"
                      style={{
                        pointerEvents: "auto",
                        zIndex: 11,
                        position: "relative",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAll();
                      }}
                    />
                  </TableCell>
                  {columns
                    .filter((col) => col.visible)
                    .map((column) =>
                      column.key === "Catogory" ? (
                        <TableCell
                          key={column.key}
                          draggable
                          onDragStart={(e) => handleDragStart(e, column.key)}
                          onDragOver={(e) => handleDragOver(e, column.key)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, column.key)}
                          onDragEnd={handleDragEnd}
                          style={{
                            whiteSpace: "nowrap",
                            backgroundColor:
                              dragOverColumn === column.key
                                ? "#e3f2fd"
                                : draggedColumn === column.key
                                ? "#f5f5f5"
                                : "#f5f5f5",
                            borderRight: "1px solid #e0e0e0",
                            fontWeight: 600,
                            fontSize: "12px",
                            padding: "8px 12px",
                            minWidth: "120px",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            cursor: isDragging ? "grabbing" : "grab",
                            opacity: draggedColumn === column.key ? 0.5 : 1,
                            transition: "all 0.2s ease",
                          }}
                        >
                          <Box display="flex" alignItems="center">
                            <Tooltip title="Drag to reorder">
                              <MdDragIndicator
                                size={16}
                                style={{
                                  marginRight: "4px",
                                  color: "#666",
                                  cursor: "grab",
                                }}
                              />
                            </Tooltip>
                            <span style={{ marginRight: "8px", color: "#666" }}>
                              {column.label}
                            </span>
                          </Box>
                        </TableCell>
                      ) : column.key === "actions" ? (
                        <TableCell
                          key={column.key}
                          draggable
                          onDragStart={(e) => handleDragStart(e, column.key)}
                          onDragOver={(e) => handleDragOver(e, column.key)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, column.key)}
                          onDragEnd={handleDragEnd}
                          style={{
                            backgroundColor:
                              dragOverColumn === column.key
                                ? "#e3f2fd"
                                : draggedColumn === column.key
                                ? "#f5f5f5"
                                : "#f5f5f5",
                            fontWeight: 600,
                            fontSize: "12px",
                            padding: "8px 12px",
                            minWidth: "80px",
                            textAlign: "center",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            cursor: isDragging ? "grabbing" : "grab",
                            opacity: draggedColumn === column.key ? 0.5 : 1,
                            transition: "all 0.2s ease",
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Tooltip title="Drag to reorder">
                              <MdDragIndicator
                                size={16}
                                style={{
                                  marginRight: "4px",
                                  color: "#666",
                                  cursor: "grab",
                                }}
                              />
                            </Tooltip>
                            {column.label}
                          </Box>
                        </TableCell>
                      ) : (
                        <TableCell
                          key={column.key}
                          draggable
                          onDragStart={(e) => handleDragStart(e, column.key)}
                          onDragOver={(e) => handleDragOver(e, column.key)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, column.key)}
                          onDragEnd={handleDragEnd}
                          onClick={() =>
                            column.sortable && handleSort(column.key)
                          }
                          style={{
                            cursor: isDragging
                              ? "grabbing"
                              : column.sortable
                              ? "pointer"
                              : "grab",
                            whiteSpace: "nowrap",
                            backgroundColor:
                              dragOverColumn === column.key
                                ? "#e3f2fd"
                                : draggedColumn === column.key
                                ? "#f5f5f5"
                                : "#f5f5f5",
                            borderRight: "1px solid #e0e0e0",
                            fontWeight: 600,
                            fontSize: "12px",
                            padding: "8px 12px",
                            minWidth:
                              column.key === "ID"
                                ? "80px"
                                : column.key === "USN"
                                ? "120px"
                                : column.key === "Name"
                                ? "120px"
                                : column.key === "Email"
                                ? "180px"
                                : column.key === "Mobile"
                                ? "120px"
                                : "100px",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            opacity: draggedColumn === column.key ? 0.5 : 1,
                            transition: "all 0.2s ease",
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box display="flex" alignItems="center">
                              <Tooltip title="Drag to reorder">
                                <MdDragIndicator
                                  size={16}
                                  style={{
                                    marginRight: "4px",
                                    color: "#666",
                                    cursor: "grab",
                                  }}
                                />
                              </Tooltip>
                              <span style={{ color: "#666" }}>
                                {column.key === "performance_percentage" &&
                                sortedStudents().length > 0
                                  ? `Progress (${
                                      sortedStudents()[0]
                                        .progress_percentage_of_should_be_completed_subtasks !=
                                      null
                                        ? `${sortedStudents()[0].progress_percentage_of_should_be_completed_subtasks}%`
                                        : "--"
                                    })`
                                  : column.key === "No_of_hours"
                                  ? `${column.label} (${totalTimeToBeSpent || "--"})`
                                  : column.label}
                              </span>
                            </Box>
                            {column.sortable && (
                              <span style={{ marginLeft: "4px" }}>
                                {sortConfig.key === column.key ? (
                                  sortConfig.direction === "ascending" ? (
                                    <PiSortAscendingLight size={16} />
                                  ) : (
                                    <PiSortDescendingLight size={16} />
                                  )
                                ) : (
                                  <HiOutlineDotsVertical size={16} />
                                )}
                              </span>
                            )}
                          </Box>
                        </TableCell>
                      )
                    )}
                </TableRow>
              </TableHead>

              <TableBody>
                {error ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.filter((col) => col.visible).length + 1}
                      align="center"
                      style={{ padding: "20px" }}
                    >
                      <Typography color="error" variant="body1">
                        {error}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.filter((col) => col.visible).length + 1}
                      align="center"
                      style={{ padding: "20px" }}
                    >
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </TableCell>
                  </TableRow>
                ) : sortedStudents().length > 0 ? (
                  sortedStudents().map((student, index) => (
                    <TableRow
                      key={student.ID || index}
                      style={{
                        backgroundColor:
                          index % 2 === 0 ? "#ffffff" : "#fafafa",
                      }}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                          cursor: "pointer",
                        },
                      }}
                    >
                      <TableCell
                        style={{
                          padding: "6px 12px",
                          textAlign: "center",
                          borderRight: "1px solid #e0e0e0",
                        }}
                      >
                        <Checkbox
                          checked={selectedStudents.has(student.ID)}
                          onChange={() => handleSelectStudent(student.ID)}
                          size="small"
                        />
                      </TableCell>
                      {columns
                        .filter((col) => col.visible)
                        .map((column) =>
                          column.key === "actions" ? (
                            <TableCell
                              key={column.key}
                              style={{
                                padding: "6px 12px",
                                textAlign: "center",
                                borderRight: "1px solid #e0e0e0",
                              }}
                            >
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleViewStudent(student)}
                                style={{
                                  fontSize: "11px",
                                  padding: "4px 12px",
                                  minWidth: "60px",
                                }}
                              >
                                VIEW
                              </Button>
                            </TableCell>
                          ) : (
                            <TableCell
                              key={column.key}
                              style={{
                                padding: "6px 12px",
                                fontSize: "12px",
                                borderRight: "1px solid #e0e0e0",
                                color: "#333",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {column.key === "No_of_hours" ? (
                                student.No_of_hours != null &&
                                student.No_of_hours !== "" ? (
                                  student.No_of_hours
                                ) : (
                                  "--"
                                )
                              ) : column.key === "performance_percentage" ? (
                                student.performance_percentage != null
                                  ? `${student.performance_percentage}%`
                                  : "--"
                              ) : column.key === "Performance_Type" ? (
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      student.Performance_Type === "ahead"
                                        ? "#4caf50"
                                        : student.Performance_Type === "laggards" ||
                                          student.Performance_Type === "behind"
                                        ? "#f44336"
                                        : "#ff9800",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {student.Performance_Type}
                                </span>
                              ) : column.key === "ID" ||
                                column.key === "USN" ||
                                column.key === "Score" ||
                                column.key === "Score_%" ? (
                                <span
                                  style={{ fontWeight: 600, color: "#1976d2" }}
                                >
                                  {column.key === "Score" ||
                                  column.key === "Score_%"
                                    ? student[column.key as keyof Student] ===
                                      "--/--"
                                      ? "--"
                                      : student[column.key as keyof Student]
                                    : student[column.key as keyof Student]}
                                </span>
                              ) : column.key === "Name" ? (
                                <span
                                  style={{ fontWeight: 500, color: "#333" }}
                                >
                                  {student[column.key as keyof Student]}
                                </span>
                              ) : column.key === "Email" ? (
                                <span
                                  style={{ color: "#666", fontSize: "11px" }}
                                >
                                  {student[column.key as keyof Student]}
                                </span>
                              ) : (
                                student[column.key as keyof Student]
                              )}
                            </TableCell>
                          )
                        )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.filter((col) => col.visible).length + 1}
                      align="center"
                      style={{
                        padding: "20px",
                        color: "#666",
                        fontSize: "14px",
                        fontStyle: "italic",
                      }}
                    >
                      {(() => {
                        const fromSidebar = location.state?.fromSidebar;
                        const hasBasicFilters =
                          selectedCourse && selectedBatch && (selectedSubject || selectedProject);

                        if (fromSidebar) {
                          if (!hasBasicFilters) {
                            return "Please select course, batch, and subject or project to view student data";
                          }
                          // Category selected and data was fetched (empty list) -> no data available
                          if (selectedCategory) {
                            return "No data available";
                          }
                          return "Please select a category to view student data";
                        } else {
                          return hasBasicFilters && selectedCategory
                            ? "No data available"
                            : "Please select course, batch, subject or project, and category to view student data";
                        }
                      })()}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Column Settings Popover */}
          <Popover
            open={Boolean(settingsAnchor)}
            anchorEl={settingsAnchor}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Box sx={{ p: 2, minWidth: 200 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Column Visibility
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const resetColumns = resetColumnVisibility(PAGE_KEYS.LEARNING_PROGRESS, getDefaultLearningProgressColumns());
                    setColumns(resetColumns);
                  }}
                  sx={{ fontSize: '12px', py: 0.5 }}
                >
                  Reset
                </Button>
              </Box>
              {columns.map((column) => (
                <FormControlLabel
                  key={column.key}
                  control={
                    <Checkbox
                      checked={column.visible}
                      onChange={() => handleColumnVisibilityChange(column.key)}
                      disabled={column.key === "actions"} // Always keep actions column visible
                    />
                  }
                  label={column.label}
                  sx={{ display: "block", mb: 1 }}
                />
              ))}
            </Box>
          </Popover>
        </CardContent>
      </Card>
    </div>
  );
};

export default LearningProgress;
